import { useDiceRollPrivacyStore } from "@/hooks/useDiceRollPrivacyStore";
import { useShared3dDiceSettingStore } from "@/hooks/useShared3dDiceSettingStore";
import type { DiceRollEventPayload, DiceSet, RollDicePayload } from "@/types";
import { lazy, Suspense, useEffect, type FC, type MouseEvent } from "react";

export const rollerImport = () =>
  import("./Roller").then(({ Roller }) => ({ default: Roller }));

const Roller = lazy(rollerImport);

export interface CustomDiceRollerProps {
  onClose: () => void;
  rollInstrumentationCallback?: (payload: DiceRollEventPayload) => void;
  rollCallback?: (payload: RollDicePayload) => void;
  rollErrorCallback?: (exception: unknown) => void;
  canUseShared3dDice?: boolean;
  userId: number;
  setId?: string;
  clearDiceCallback?: () => void;
  rollToCallback?: (e: MouseEvent<HTMLButtonElement>) => void;
  diceSets: DiceSet[];
  gameId?: number;
  preview?: boolean;
  customDiceSet?: boolean;
  visible?: boolean;
  entityType?: string;
  entityId?: string;
  messageTarget?: number;
  name?: string;
}

export const CustomDiceRoller: FC<CustomDiceRollerProps> = ({
  canUseShared3dDice,
  userId,
  visible,
  ...rest
}) => {
  const { getIsShared3dDiceSettingEnabled, setIsShared3dDiceSettingEnabled } =
    useShared3dDiceSettingStore();
  const { getDiceRollPrivacy, setHasSeenDiceContainer, setRollToPrivacy } =
    useDiceRollPrivacyStore();

  useEffect(() => {
    if (
      getIsShared3dDiceSettingEnabled(Boolean(canUseShared3dDice), userId) ===
      undefined
    ) {
      setIsShared3dDiceSettingEnabled(userId);
    }
  }, [
    canUseShared3dDice,
    getIsShared3dDiceSettingEnabled,
    setIsShared3dDiceSettingEnabled,
    userId,
  ]);

  useEffect(() => {
    const diceContainerConfigJson = JSON.parse(
      localStorage.getItem("diceContainerConfig") || "{}",
    );
    const diceContainerConfigUserSettings = diceContainerConfigJson[userId];

    // migrate old dice container privacy settings
    if (diceContainerConfigUserSettings) {
      if (diceContainerConfigUserSettings.hasSeenDiceContainer) {
        setHasSeenDiceContainer(userId);
      }

      if (diceContainerConfigUserSettings.rollTo) {
        setRollToPrivacy(userId, diceContainerConfigUserSettings.rollTo);
      }

      // remove old settings after migration
      delete diceContainerConfigJson[userId];

      const userConfigsCount = Object.keys(diceContainerConfigJson).length;

      if (userConfigsCount) {
        localStorage.setItem(
          "diceContainerConfig",
          JSON.stringify(diceContainerConfigJson),
        );
      } else {
        localStorage.removeItem("diceContainerConfig");
      }
    }

    if (!getDiceRollPrivacy(userId)) {
      setRollToPrivacy(userId);
    }
  }, [getDiceRollPrivacy, setHasSeenDiceContainer, setRollToPrivacy, userId]);

  if (!visible) return null;

  return (
    <Suspense>
      <Roller
        canUseShared3dDice={canUseShared3dDice}
        userId={userId}
        {...rest}
      />
    </Suspense>
  );
};
